import { faChartColumn, faGear, faPenToSquare } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useSearchParams } from "react-router-dom"
import { PopupHelpButton } from "../../../components/Help/PopupHelpButton"
import List from "../../../components/List/List"
import { useTimeClock } from "../../../hooks/TimeClock/useTimeClock"
import { getTimeClockEmployeeCard } from "../../../Objects/TimeClock/TimeClockCards"
import type { TimeClockSettings } from "../../../Objects/TimeClock/TimeClockSettings"
import type { CreateTimeClockAdjustment } from "../../../Objects/TimeClock/CreateTimeClockAdjustment"
import type { TimeClockLiveEmployee } from "../../../Objects/TimeClock/TimeClockLiveEmployee"
import type { TimeClockManagement } from "../../../Objects/TimeClock/TimeClockManagement"
import type { TimeClockEmployeeSummary } from "../../../Objects/TimeClock/TimeClockEmployeeSummary"
import type { EmployeeWorkSchedule } from "../../../Objects/TimeClock/EmployeeWorkSchedule"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import { TimeClockAdjustmentPopup } from "../../TimeClock/TimeClockAdjustmentPopup"
import { TimeClockAdjustmentsPopup } from "./TimeClockAdjustmentsPopup"
import { TimeClockEmployeePopup } from "./TimeClockEmployeePopup"
import { TimeClockPinPopup } from "./TimeClockPinPopup"
import { TimeClockReportsPopup } from "./TimeClockReportsPopup"
import { TimeClockSchedulePopup } from "./TimeClockSchedulePopup"
import { TimeClockSettingsPopup } from "./TimeClockSettingsPopup"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"

const quickActionPopups = {
  "time-clock-adjustments": "adjustments",
  "time-clock-reports": "reports",
  "time-clock-settings": "settings",
} as const

export const TimeClockPage = () => {
  const { t, i18n } = useTranslation(["timeClock", "helpPopup"])

  const [searchParams] = useSearchParams()

  const { getManagement, saveSettings, saveSchedule, savePin, getLive, createManagementAdjustment, reviewAdjustment } =
    useTimeClock()

  const [management, setManagement] = useState<TimeClockManagement | undefined>(undefined)

  const [live, setLive] = useState<TimeClockLiveEmployee[]>([])

  const [selectedEmployee, setSelectedEmployee] = useState<TimeClockEmployeeSummary | null>(null)

  const [scheduleEmployee, setScheduleEmployee] = useState<TimeClockEmployeeSummary | null>(null)

  const [pinEmployee, setPinEmployee] = useState<TimeClockEmployeeSummary | null>(null)

  const [isSettingsOpen, setIsSettingsOpen] = useState(false)

  const [isAdjustmentsOpen, setIsAdjustmentsOpen] = useState(false)

  const [isReportsOpen, setIsReportsOpen] = useState(false)

  const [isNewAdjustmentOpen, setIsNewAdjustmentOpen] = useState(false)

  const load = useCallback(async () => {
    const [managementResult, liveResult] = await Promise.all([getManagement(), getLive()])

    if (managementResult) setManagement(managementResult)

    if (liveResult) setLive(liveResult)
  }, [])

  useEffect(() => {
    void load()
  }, [])

  useEffect(() => {
    if (!management) return

    setSelectedEmployee((current) => {
      if (!current) return current

      const fresh = management.employees.find((employee) => employee.employeeId === current.employeeId)

      return fresh ?? current
    })
  }, [management])

  useEffect(() => {
    const quickAction = searchParams.get("quickAction")

    if (!quickAction || !(quickAction in quickActionPopups)) {
      return
    }

    const target = quickActionPopups[quickAction as keyof typeof quickActionPopups]

    if (target === "adjustments") setIsAdjustmentsOpen(true)

    if (target === "reports") setIsReportsOpen(true)

    if (target === "settings") setIsSettingsOpen(true)
  }, [searchParams])

  const employees = management?.employees ?? []

  const liveByEmployee = useMemo(() => new Map(live.map((item) => [item.employeeId, item])), [live])

  const cards = useMemo(
    () =>
      employees.map((employee, index) =>
        getTimeClockEmployeeCard(employee, liveByEmployee.get(employee.employeeId), index, t, i18n.language),
      ),
    [employees, liveByEmployee, t, i18n.language],
  )

  const handleSaveSettings = async (settings: TimeClockSettings) => {
    const saved = await saveSettings(settings)

    if (!saved) return false

    setIsSettingsOpen(false)

    await load()

    return true
  }

  const handleSaveSchedule = async (days: EmployeeWorkSchedule[]) => {
    if (!scheduleEmployee) return false

    const saved = await saveSchedule({ employeeId: scheduleEmployee.employeeId, days })

    if (!saved) return false

    setScheduleEmployee(null)

    await load()

    return true
  }

  const handleSavePin = async (pin: string) => {
    if (!pinEmployee) return false

    const saved = await savePin({ employeeId: pinEmployee.employeeId, pin })

    if (!saved) return false

    setPinEmployee(null)

    await load()

    return true
  }

  const handleCreateAdjustment = async (request: CreateTimeClockAdjustment) => {
    const created = await createManagementAdjustment(request)

    if (!created) return false

    setIsNewAdjustmentOpen(false)

    await load()

    return true
  }

  const handleReviewAdjustment = async (id: number, isApproved: boolean, reviewNotes?: string) => {
    const reviewed = await reviewAdjustment(id, { isApproved, reviewNotes })

    if (!reviewed) return false

    await load()

    return true
  }

  return (
    <>
      <div className={defaultStyle.containerList}>
        <List
          enableLocalPagination
          messageCount={t("timeClock:management.employees_count")}
          messageEmpty={t("timeClock:management.empty_employees")}
          nameFindSearch={t("timeClock:management.search_employees")}
          data={cards}
          nameDropdown={[
            t("timeClock:management.working_now"),
            t("timeClock:management.not_working"),
            t("timeClock:management.all_status"),
          ]}
          defaultStatusFilter={t("timeClock:management.all_status")}
          handleRefreshButton={() => void load()}
          handleExtraFloatButton={() => setIsSettingsOpen(true)}
          extraFloatButtonIcon={faGear}
          extraFloatButtonColor="#0ea5e9"
          extraFloatButtonDescriptionKey="interactionDescriptions:pages.myRestaurant.timeClock.timeClockPage.button.openSettings"
          handleSecondaryExtraFloatButton={() => setIsAdjustmentsOpen(true)}
          secondaryExtraFloatButtonIcon={faPenToSquare}
          secondaryExtraFloatButtonColor="#8b5cf6"
          secondaryExtraFloatButtonDescriptionKey="interactionDescriptions:pages.myRestaurant.timeClock.timeClockPage.button.openAdjustments"
          handleImportFloatButton={() => setIsReportsOpen(true)}
          importFloatButtonIcon={faChartColumn}
          importFloatButtonColor="#16a34a"
          importFloatButtonDescriptionKey="interactionDescriptions:pages.myRestaurant.timeClock.timeClockPage.button.openReports"
          handleOnClick={(id) => setSelectedEmployee(employees[id] ?? null)}
          countFooter={
            management && (
              <>
                {!management.settings.isEnabled && (
                  <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
                    <div className="w-full flex items-center gap-3 rounded-lg px-4 py-1 shadow-sm">
                      <div className="text-amber-600 text-lg">
                        <FontAwesomeIcon icon={faGear} />
                      </div>

                      <span className="text-sm font-semibold text-slate-700">
                        {t("timeClock:management.disabled_warning")}
                      </span>
                    </div>
                  </Animate>
                )}

                {management.pendingAdjustmentCount > 0 && (
                  <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
                    <div className="w-full flex items-center gap-3 rounded-lg px-4 py-1 shadow-sm">
                      <div className="text-amber-600 text-lg">
                        <FontAwesomeIcon icon={faPenToSquare} />
                      </div>

                      <span className="text-base font-bold text-amber-600">{management.pendingAdjustmentCount}</span>

                      <span className="text-sm font-semibold text-slate-700">
                        {management.pendingAdjustmentCount === 1
                          ? t("timeClock:management.pending_adjustments_label_one")
                          : t("timeClock:management.pending_adjustments_label_other")}
                      </span>
                    </div>
                  </Animate>
                )}
              </>
            )
          }
          filterActions={
            <PopupHelpButton
              variant="global"
              helpKey="pages.time-clock"
              title={t("helpPopup:global.timeClock.title")}
              description={t("helpPopup:global.timeClock.description")}
              bullets={t("helpPopup:global.timeClock.bullets", { returnObjects: true }) as string[]}
              steps={t("helpPopup:global.timeClock.steps", { returnObjects: true }) as string[]}
            />
          }
        />
      </div>

      {selectedEmployee && management && (
        <TimeClockEmployeePopup
          employee={selectedEmployee}
          live={liveByEmployee.get(selectedEmployee.employeeId)}
          scheduleModel={management.settings.scheduleModel}
          onClose={() => setSelectedEmployee(null)}
          onEditSchedule={() => setScheduleEmployee(selectedEmployee)}
          onEditPin={() => setPinEmployee(selectedEmployee)}
          onCreateAdjustment={() => setIsNewAdjustmentOpen(true)}
          onReviewed={load}
        />
      )}

      {isSettingsOpen && management && (
        <TimeClockSettingsPopup
          settings={management.settings}
          onClose={() => setIsSettingsOpen(false)}
          onSave={handleSaveSettings}
        />
      )}

      {scheduleEmployee && management && (
        <TimeClockSchedulePopup
          employee={scheduleEmployee}
          scheduleModel={management.settings.scheduleModel}
          onClose={() => setScheduleEmployee(null)}
          onSave={handleSaveSchedule}
        />
      )}

      {pinEmployee && (
        <TimeClockPinPopup employee={pinEmployee} onClose={() => setPinEmployee(null)} onSave={handleSavePin} />
      )}

      {isAdjustmentsOpen && (
        <TimeClockAdjustmentsPopup
          onClose={() => setIsAdjustmentsOpen(false)}
          onReview={handleReviewAdjustment}
          onCreate={() => {
            setIsAdjustmentsOpen(false)
            setIsNewAdjustmentOpen(true)
          }}
        />
      )}

      {isReportsOpen && <TimeClockReportsPopup employees={employees} onClose={() => setIsReportsOpen(false)} />}

      {isNewAdjustmentOpen && (
        <TimeClockAdjustmentPopup
          isManagement
          entries={[]}
          employees={employees.map((employee) => ({
            employeeId: employee.employeeId,
            employeeName: employee.employeeName,
          }))}
          onClose={() => setIsNewAdjustmentOpen(false)}
          onSave={handleCreateAdjustment}
        />
      )}
    </>
  )
}
